// WaveChart — premium sine-wave renderer used across the Sound Waves
// lesson. Built on Chart.js (line chart with showLine + tension) per the
// project's "smooth scatter" rule (pointRadius: 0).
//
// The chart accepts an array of `series` so we can:
//   • show one sine wave (slide 2)
//   • compare two waves (slide 5 — low vs high pitch)
//   • highlight a slice of a wave with a thicker overlay (slide 4 — T)
//   • morph amplitudes between fragments (slide 6)
// plus optional measurement brackets (slide 3 — λ) and text labels
// (slide 2 — crest/trough markers) drawn via Chart.js plugins.

import React, { useMemo } from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(...registerables);

/** Generate y = f(x) samples between xMin..xMax in `step` */
export const sampleSine = (
    fn,
    { xMin = 0, xMax = 10, step = 0.05 } = {},
) => {
    const pts = [];
    for (let x = xMin; x <= xMax + 1e-9; x += step) {
        pts.push({ x: Number(x.toFixed(4)), y: fn(x) });
    }
    return pts;
};

/**
 * Custom Chart.js plugin to draw measurement brackets + arbitrary text
 * overlays directly on the canvas. We avoid chartjs-plugin-annotation
 * because we want full control over the bracket end-caps and animated
 * fade-in.
 */
const overlayPlugin = {
    id: "sparkl-sound-overlay",
    afterDatasetsDraw(chart, _args, opts) {
        if (!opts) return;
        const { brackets = [], labels = [] } = opts;
        const { ctx, scales } = chart;
        const xs = scales.x;
        const ys = scales.y;

        ctx.save();
        for (const b of brackets) {
            if (b.opacity === 0) continue;
            const x1 = xs.getPixelForValue(b.x1);
            const x2 = xs.getPixelForValue(b.x2);
            const y = ys.getPixelForValue(b.y);
            ctx.globalAlpha = b.opacity ?? 1;
            ctx.strokeStyle = b.color || "#FFA907";
            ctx.lineWidth = 3;
            ctx.lineCap = "round";
            // horizontal line
            ctx.beginPath();
            ctx.moveTo(x1, y);
            ctx.lineTo(x2, y);
            ctx.stroke();
            // end caps
            const cap = 12;
            ctx.beginPath();
            ctx.moveTo(x1, y - cap);
            ctx.lineTo(x1, y + cap);
            ctx.moveTo(x2, y - cap);
            ctx.lineTo(x2, y + cap);
            ctx.stroke();
            // label
            if (b.label) {
                ctx.font =
                    "600 22px ui-sans-serif, system-ui, -apple-system, 'Inter', sans-serif";
                ctx.fillStyle = b.color || "#FFA907";
                ctx.textAlign = "center";
                ctx.textBaseline = b.labelBelow ? "top" : "bottom";
                const labelY = b.labelBelow ? y + cap + 8 : y - cap - 8;
                ctx.fillText(b.label, (x1 + x2) / 2, labelY);
            }
        }

        for (const lab of labels) {
            if (lab.opacity === 0) continue;
            const px = xs.getPixelForValue(lab.x);
            const py = ys.getPixelForValue(lab.y);
            ctx.globalAlpha = lab.opacity ?? 1;
            ctx.fillStyle = lab.color || "#14202a";
            ctx.font =
                lab.font ||
                "600 18px ui-sans-serif, system-ui, -apple-system, 'Inter', sans-serif";
            ctx.textAlign = lab.align || "center";
            ctx.textBaseline = lab.baseline || "middle";
            ctx.fillText(lab.text, px + (lab.dx || 0), py + (lab.dy || 0));
        }
        ctx.restore();
    },
};

ChartJS.register(overlayPlugin);

/**
 * series[] entries:
 *   { id, label, color, borderWidth?, points: [{x,y}], visible?: true }
 */
const WaveChart = ({
    series = [],
    xMin,
    xMax,
    yMin,
    yMax,
    xTitle,
    yTitle,
    xTickStep,
    yTickStep,
    hideXTicks = false,
    hideYTicks = false,
    brackets = [],
    labels = [],
    aspect = 1,
}) => {
    const data = useMemo(
        () => ({
            datasets: series
                .filter((s) => s.visible !== false)
                .map((s) => ({
                    label: s.label,
                    data: s.points,
                    showLine: true,
                    borderColor: s.color,
                    backgroundColor: s.color,
                    borderWidth: s.borderWidth ?? 3,
                    pointRadius: 0,
                    tension: 0.4,
                    parsing: false,
                    spanGaps: true,
                })),
        }),
        [series],
    );

    const options = useMemo(
        () => ({
            animation: { duration: 600, easing: "easeOutQuart" },
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
                "sparkl-sound-overlay": { brackets, labels },
            },
            scales: {
                x: {
                    type: "linear",
                    min: xMin,
                    max: xMax,
                    title: xTitle
                        ? {
                              display: true,
                              text: xTitle,
                              color: "#4a5662",
                              font: { size: 16, weight: "600" },
                          }
                        : { display: false },
                    grid: { color: "rgba(20,32,42,0.06)" },
                    ticks: {
                        display: !hideXTicks,
                        color: "rgba(20,32,42,0.55)",
                        font: { size: 14 },
                        stepSize: xTickStep,
                    },
                },
                y: {
                    type: "linear",
                    min: yMin,
                    max: yMax,
                    title: yTitle
                        ? {
                              display: true,
                              text: yTitle,
                              color: "#4a5662",
                              font: { size: 16, weight: "600" },
                          }
                        : { display: false },
                    grid: { color: "rgba(20,32,42,0.06)" },
                    ticks: {
                        display: !hideYTicks,
                        color: "rgba(20,32,42,0.55)",
                        font: { size: 14 },
                        stepSize: yTickStep,
                    },
                },
            },
        }),
        [
            xMin,
            xMax,
            yMin,
            yMax,
            xTitle,
            yTitle,
            xTickStep,
            yTickStep,
            hideXTicks,
            hideYTicks,
            brackets,
            labels,
        ],
    );

    return (
        <div style={{ width: "100%", height: "100%", position: "relative" }}>
            <Line data={data} options={options} />
        </div>
    );
};

export default WaveChart;
